import React from 'react';
import {Form, Col, Row} from 'react-bootstrap';
import {BaseSettingsForm} from '@/components/forms';
import {Button} from '@/components/ui';
import axios, {AxiosResponse} from 'axios';
import * as yup from 'yup';
import JSONPretty from 'react-json-prettify';
import {useStoreActions} from 'store';
import {Story} from 'entities/story';
import i18n from 'i18n';


interface Props {
    onDone: () => void;
}

interface StoryImportPostData {
    json: string;
}

const StoryImportFormSchema: yup.ObjectSchema<StoryImportPostData> = yup.object({
    json: yup.string().required(),
});

const StoryImportForm = React.memo<Props>((
    {
        onDone,
    },
) => {
    const addOrEditStory = useStoreActions((actions) => actions.stories.addOrEditStory);

    const initialValues: StoryImportPostData = {
        json: '',
    };

    const customSubmissionHandler = async (values: StoryImportPostData) => {
        const response = await axios.post<Story, AxiosResponse<Story, StoryImportPostData>, StoryImportPostData>(
            window.URLS.storiesImportResource,
            values
        );

        addOrEditStory(response.data);
        onDone();

        return response;
    };

    return (
        <BaseSettingsForm
            resourceUrl="" // Not used with customSubmissionHandler
            storeActionPath="" // Not used with customSubmissionHandler
            validationSchema={StoryImportFormSchema}
            initialValues={initialValues}
            successMessageKey="jt_story_saved.text"
            customSubmissionHandler={customSubmissionHandler}
        >
            {(formik, loading) => {
                let json;
                try {
                    json = JSON.parse(formik.values.json);
                } catch (e) {
                    json = '';
                }

                return (
                    <>
                        {(formik.values.json === '' || json?.json_version === '1.1.0') ? null : (
                            <div className="alert alert-danger text-left">
                                {i18n.t('jt_the_payloads.text')}
                            </div>
                        )}

                        <Row>
                            <Col>
                                <Form.Group className="mb-3" controlId="storyJson" >
                                    <Form.Label>{i18n.t('jt_json.text')}</Form.Label>
                                    <Form.Control
                                        name="json"
                                        as="textarea"
                                        rows={20}
                                        onChange={formik.handleChange}
                                        onBlur={formik.handleBlur}
                                        value={formik.values.json}
                                        isInvalid={formik.touched.json && !!formik.errors.json}
                                        placeholder='{}'
                                        autoFocus
                                    />
                                    <Form.Control.Feedback type="invalid">
                                        {formik.errors.json}
                                    </Form.Control.Feedback>
                                </Form.Group>
                            </Col>
                            <Col>
                                <Form.Group className="mb-3" controlId="storyJsonPreview" id="json-pretty" >
                                    <Form.Label>{i18n.t('jt_formatted_json.text')}</Form.Label>
                                    <JSONPretty json={json} />
                                </Form.Group>
                            </Col>
                        </Row>

                        <Row className="mt-4">
                            <Col xs={12} className="d-flex justify-content-end">
                                <Button
                                    type="submit"
                                    variant="primary"
                                    disabled={formik.isSubmitting || !formik.isValid}
                                    loading={formik.isSubmitting}
                                    data-cy="story-import-save-btn"
                                >
                                    {i18n.t('jt_import_story.text')}
                                </Button>
                            </Col>
                        </Row>
                    </>
                );
            }}
        </BaseSettingsForm >
    );
});




export default StoryImportForm;
